import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import {
  EthernetPortIcon,
  FactoryIcon,
  GlobeIcon,
  HistoryIcon,
  MapPinHouseIcon,
  Router,
  RouterIcon,
  UserCircle2Icon,
  UserIcon,
  XIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { InfoStack, TextStack, Title, Value } from './styles';
import Card from '@/components/Card';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import { formatIsoDate } from '@/utils/timeFormatter';
import { useTheme } from '@mui/material/styles';
import { Button } from '@/components/Button';
import { DataDisplay, DataStack, HeaderStack, TitleStack } from '../styles';
function CustomerCard({
  info,
  open,
  setOpen,
}: {
  info: DeviceType;
  open: boolean;
  setOpen: () => void;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const deviceData = [
    {
      label: t('map.card.device'),
      value: info.model.alias,
      icon: <RouterIcon size={16} />,
    },
    {
      label: t('map.card.manufacturer'),
      value: info.manufacturer,
      icon: <FactoryIcon size={16} />,
    },
    {
      label: t('map.card.ip'),
      value: info.ipAddress,
      icon: <GlobeIcon size={16} />,
    },
    {
      label: t('map.card.macAddress'),
      value: info.customerData?.lanMacAddress,
      icon: <EthernetPortIcon size={16} />,
    },
    {
      label: t('map.card.lastInform'),
      value: formatIsoDate(info.lastInform),
      icon: <HistoryIcon size={16} />,
    },
  ];

  const customerData = [
    {
      label: t('map.card.username'),
      value: info.customerData?.userPPP,
      icon: <UserIcon size={16} />,
    },
    {
      label: t('map.card.name'),
      value: info.customerData?.customerName,
      icon: <UserIcon size={16} />,
    },
    {
      label: t('map.card.address'),
      value: info.customerData?.address,
      icon: <MapPinHouseIcon size={16} />,
    },
  ];

  return (
    <DataDisplay $open={open}>
      <Card sx={{ height: '100%', padding: 2 }}>
        {/** Header with a close button */}
        <HeaderStack>
          <TitleStack>
            <RouterIcon size={20} />
            <Typography variant="h5">{t('map.card.deviceTitle')}</Typography>
          </TitleStack>
          <IconButton onClick={() => setOpen()}>
            <XIcon />
          </IconButton>
        </HeaderStack>

        {/** Shows boolean informations in Chips */}
        <TitleStack sx={{ my: 2, mt: 1 }}>
          <Chip
            label={info.model.ont ? t('map.card.ont') : t('map.card.router')}
            color={info.model.ont ? 'primary' : 'secondary'}
            sx={{
              color: info.model.ont
                ? theme.palette.fixed.white
                : theme.palette.secondary.contrastText,
            }}
          />
          <Chip
            label={info.online ? t('map.card.online') : t('map.card.offline')}
            color={info.online ? 'success' : 'error'}
          />
        </TitleStack>

        {/** Customer current device information */}
        <DataStack>
          {deviceData.map((item) => (
            <InfoStack key={item.label}>
              {item.icon}
              <TextStack>
                <Title>{item.label}</Title>
                <Value>{item.value}</Value>
              </TextStack>
            </InfoStack>
          ))}

          <Divider variant="fullWidth" sx={{ width: '100%', my: '8px' }} />

          {/** Customer Information */}
          <TitleStack>
            <UserCircle2Icon size={20} />
            <Typography variant="h5">{t('map.card.customerTitle')}</Typography>
          </TitleStack>
          {customerData.map((item) => (
            <InfoStack key={item.label}>
              {item.icon}
              <TextStack>
                <Title>{item.label}</Title>
                <Value>{item.value}</Value>
              </TextStack>
            </InfoStack>
          ))}

          <Button
            onClick={() => navigate(`/equipment/${info.id}`)}
            sx={{ width: '100%' }}
            startIcon={<Router size={18} />}
          >
            {t('map.card.seeEquipment')}
          </Button>
        </DataStack>
      </Card>
    </DataDisplay>
  );
}

export default CustomerCard;
